import { useMemo } from 'react';

import {
  ViewProps,
  FillInBlanksSentence as FillInBlanksSentenceType,
  FillInBlanksAnswer,
} from './fill-in-blanks.constants';
import SubActivityView from '../sub-activity/sub-activity-view';
import { sortFillInBlanksSentences } from './logic/sort-fill-in-blanks-sentences';
import { mergeFillInBlanksBlocks } from './logic/merge-fill-in-blanks-blocks';
import { getFillInBlanksAnswerColour } from './logic/get-fill-in-blanks-answer-colour';
import {
  FillInBlanksSentence,
  FillInBlanksSentencesWrapper,
} from './fill-in-blanks-shared.style';
import FillInBlanksTextBlock from './fill-in-blanks-text-block';
import * as Styled from './fill-in-blanks-preview.style';

const AnswerBlock = ({
  answer,
  fontSize,
  fontFamily,
}: {
  answer: FillInBlanksAnswer;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const colours = useMemo(() => getFillInBlanksAnswerColour(answer), [answer]);

  return (
    <Styled.FillInBlanksField
      $colours={colours}
      $minWidth={answer.width}
      $punctuation={answer.punctuation}
      $hasLabel={!!(answer.showLabel && answer.label)}
      $fontSize={fontSize}
      $fontFamily={fontFamily}
    >
      <Styled.FillInBlanksFieldInner
        $colours={colours}
        $label={answer.showLabel ? answer.label : null}
        $fontSize={fontSize}
        $fontFamily={fontFamily}
      >
        <Styled.FillInBlanksPreviewTipTap
          $placeholder={answer.placeholder}
          $isScaffold={answer.showPlaceholderAsScaffold}
          $fontSize={fontSize}
          $fontFamily={fontFamily}
        >
          <Styled.FillInBlanksFieldValue className="tiptap">
            <p>
              <br className="ProseMirror-trailingBreak" />
            </p>
          </Styled.FillInBlanksFieldValue>
        </Styled.FillInBlanksPreviewTipTap>
      </Styled.FillInBlanksFieldInner>
    </Styled.FillInBlanksField>
  );
};

const Sentence = ({
  sentence,
  fontSize,
  fontFamily,
}: {
  sentence: FillInBlanksSentenceType;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );
  return mergedBlocks.map(block => {
    return 'text' in block ? (
      <FillInBlanksTextBlock key={block.itemId} textBlock={block} />
    ) : (
      <AnswerBlock
        key={block.itemId}
        answer={block}
        fontSize={fontSize}
        fontFamily={fontFamily}
      />
    );
  });
};

const FillInBlanksPreview = ({
  properties,
  subActivityProperties,
}: ViewProps) => {
  const sortedSentences = useMemo(
    () => sortFillInBlanksSentences(properties.fillInBlanks.sentences),
    [properties.fillInBlanks.sentences]
  );

  return (
    <SubActivityView subActivityDetails={subActivityProperties}>
      <FillInBlanksSentencesWrapper
        $fontFamily={properties.fillInBlanks.fontFamily}
        $fontSize={properties.fillInBlanks.fontSize}
      >
        {sortedSentences.map(sentence => (
          <FillInBlanksSentence key={sentence.itemId}>
            <Sentence
              sentence={sentence}
              fontSize={properties.fillInBlanks.fontSize}
              fontFamily={properties.fillInBlanks.fontFamily}
            />
          </FillInBlanksSentence>
        ))}
      </FillInBlanksSentencesWrapper>
    </SubActivityView>
  );
};

export default FillInBlanksPreview;
